import React, { useState, useEffect, useCallback, useRef } from 'react'
import { Modal } from 'antd'
import { toast } from 'react-toastify'
import {
  FiSearch,
  FiUpload,
  FiChevronDown,
  FiChevronRight,
  FiFile,
  FiDownload,
  FiTrash2,
  FiPlus,
  FiFolder,
} from 'react-icons/fi'
import {
  getProveedores,
  postCrearProveedor,
  getCatalogosPorProveedor,
  postCrearCatalogo,
  deleteCatalogo,
  getIam,
  getIamRol,
  getGruposCompras,
} from 'src/services/axiomaService'
import CatalogoDetalle from 'src/components/Axioma/BovedaDocs/CatalogoDetalle.jsx'
import './AxiomaBovedaDocsContainer.css'

const { confirm } = Modal

const AxiomaBovedaDocsContainer = () => {
  const [proveedores, setProveedores] = useState([])
  const [proveedoresExpandidos, setProveedoresExpandidos] = useState({})
  const [catalogosPorProveedor, setCatalogosPorProveedor] = useState({})
  const [busqueda, setBusqueda] = useState('')
  const [modalSubirVisible, setModalSubirVisible] = useState(false)
  const [modalProveedorVisible, setModalProveedorVisible] = useState(false)
  const [archivoSeleccionado, setArchivoSeleccionado] = useState(null)
  const [proveedorSeleccionado, setProveedorSeleccionado] = useState('')
  const [nuevoProveedorNombre, setNuevoProveedorNombre] = useState('')
  const [loadingUpload, setLoadingUpload] = useState(false)
  const [loadingProveedor, setLoadingProveedor] = useState(false)
  const [idUser, setIdUser] = useState(null)
  const [catalogoSeleccionado, setCatalogoSeleccionado] = useState(null)
  const [proveedorCatalogoSeleccionado, setProveedorCatalogoSeleccionado] = useState(null)
  const [loadingBusqueda, setLoadingBusqueda] = useState(false)
  const [todosProveedores, setTodosProveedores] = useState([])
  const [modeloSeleccionado, setModeloSeleccionado] = useState('')
  const [gruposCompras, setGruposCompras] = useState([])
  const [grupoComprasSeleccionado, setGrupoComprasSeleccionado] = useState('')
  const [loadingGruposCompras, setLoadingGruposCompras] = useState(false)
  const [planificadorSeleccionado, setPlanificadorSeleccionado] = useState('')
  const [paisOrigenSeleccionado, setPaisOrigenSeleccionado] = useState('China')
  const debounceTimeout = useRef(null)

  const cargarTodosProveedores = async () => {
    const response = await getProveedores()
    if (response && response.success) {
      setTodosProveedores(response.success)
      return response.success
    }
    toast.error('Error al cargar los proveedores')
    return []
  }

  const buscarProveedores = useCallback(async (termino) => {
    if (!termino || termino.trim().length < 2) {
      setProveedores([])
      return
    }

    setLoadingBusqueda(true)
    try {
      // Si no tenemos todos los proveedores cargados, cargarlos primero
      let listaProveedores = todosProveedores
      if (listaProveedores.length === 0) {
        listaProveedores = await cargarTodosProveedores()
      }

      // Filtrar localmente
      const resultados = listaProveedores.filter((proveedor) => {
        const nombreProveedor = proveedor.nombre_proveedor || proveedor.nombre || ''
        const codigoProveedor = proveedor.codigo_proveedor || ''
        return (
          nombreProveedor.toLowerCase().includes(termino.toLowerCase()) ||
          codigoProveedor.toLowerCase().includes(termino.toLowerCase())
        )
      })

      setProveedores(resultados)
    } catch (error) {
      toast.error('Error al buscar proveedores')
    } finally {
      setLoadingBusqueda(false)
    }
  }, [todosProveedores])

  const handleBusquedaChange = (e) => {
    const valor = e.target.value
    setBusqueda(valor)

    // Limpiar el timeout anterior si existe
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current)
    }

    // Establecer un nuevo timeout para buscar después de 500ms
    debounceTimeout.current = setTimeout(() => {
      buscarProveedores(valor)
    }, 500)
  }

  const fetchIam = async () => {
    const response = await getIam()
    if (response && response.id) {
      setIdUser(response.id)
    }
  }

  useEffect(() => {
    fetchIam()
    // NO cargamos proveedores al inicio
  }, [])

  const cargarProveedoresParaModal = async () => {
    if (todosProveedores.length === 0) {
      await cargarTodosProveedores()
    }
  }

  const cargarCatalogosPorProveedor = async (nombreProveedor) => {
    if (catalogosPorProveedor[nombreProveedor]) {
      return
    }

    const response = await getCatalogosPorProveedor(nombreProveedor)
    if (response && response.success) {
      setCatalogosPorProveedor((prev) => ({
        ...prev,
        [nombreProveedor]: response.success,
      }))
    } else {
      toast.error('Error al cargar los catálogos')
    }
  }

  const toggleProveedor = async (nombreProveedor) => {
    const isExpanded = proveedoresExpandidos[nombreProveedor]
    
    if (!isExpanded) {
      await cargarCatalogosPorProveedor(nombreProveedor)
    }

    setProveedoresExpandidos((prev) => ({
      ...prev,
      [nombreProveedor]: !isExpanded,
    }))
  }

  const handleCrearProveedor = async () => {
    if (!nuevoProveedorNombre.trim()) {
      toast.error('Por favor ingresa un nombre para el proveedor')
      return
    }

    setLoadingProveedor(true)
    const response = await postCrearProveedor(nuevoProveedorNombre)
    setLoadingProveedor(false)

    if (response && response.success) {
      toast.success('Proveedor creado exitosamente')
      setModalProveedorVisible(false)
      setNuevoProveedorNombre('')
      // Limpiar caché de proveedores para forzar recarga
      setTodosProveedores([])
      // Si hay búsqueda activa, recargar resultados
      if (busqueda.trim()) {
        buscarProveedores(busqueda)
      }
    } else {
      toast.error('Error al crear el proveedor')
    }
  }

  const handleSubirCatalogo = async () => {
    if (!archivoSeleccionado) {
      toast.error('Por favor selecciona un archivo PDF')
      return
    }

    if (!proveedorSeleccionado) {
      toast.error('Por favor selecciona un proveedor')
      return
    }

    if (!modeloSeleccionado) {
      toast.error('Por favor selecciona un modelo de aplicación')
      return
    }

    

    if (!idUser) {
      toast.error('No se pudo obtener el ID del usuario')
      return
    }

    // Obtener el código del proveedor seleccionado
    const codigoProveedor = proveedorSeleccionado.codigo_proveedor || ''
    const nombreProveedor = proveedorSeleccionado.nombre_proveedor || proveedorSeleccionado.nombre
    const modeloAplicacion = modeloSeleccionado

    setLoadingUpload(true)
    const response = await postCrearCatalogo(archivoSeleccionado, codigoProveedor, nombreProveedor, idUser, modeloAplicacion, grupoComprasSeleccionado, planificadorSeleccionado, paisOrigenSeleccionado)
    setLoadingUpload(false)

    if (response && response.archivo_subido) {
      toast.success('Catálogo subido exitosamente')
      
      const proveedorParaActualizar = nombreProveedor
      
      // Cerrar modal y limpiar formulario
      setModalSubirVisible(false)
      setArchivoSeleccionado(null)
      setProveedorSeleccionado('')
      setModeloSeleccionado('')
      setGrupoComprasSeleccionado('')
      setPlanificadorSeleccionado('')
      setPaisOrigenSeleccionado('China')
      
      // Reset del input file para permitir seleccionar el mismo archivo después
      const fileInput = document.getElementById('file-upload')
      if (fileInput) {
        fileInput.value = null
      }
      
      // Limpiar caché de catálogos del proveedor
      setCatalogosPorProveedor((prev) => {
        const updated = { ...prev }
        delete updated[proveedorParaActualizar]
        return updated
      })
      
      // Expandir el acordeón del proveedor automáticamente
      setProveedoresExpandidos((prev) => ({
        ...prev,
        [proveedorParaActualizar]: true,
      }))
      
      // Recargar los catálogos para mostrar el nuevo
      await cargarCatalogosPorProveedor(proveedorParaActualizar)
    } else {
      toast.error('Error al subir el catálogo')
    }
  }

  const handleEliminarCatalogo = (path, nombreArchivo, nombreProveedor) => {
    confirm({
      title: '¿Estás seguro de eliminar este catálogo?',
      content: `Se eliminará "${nombreArchivo}" permanentemente.`,
      okText: 'Eliminar',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: async () => {
        const response = await deleteCatalogo(path)
        if (response && response.success) {
          toast.success('Catálogo eliminado exitosamente')
          
          // Actualizar la lista localmente
          setCatalogosPorProveedor((prev) => ({
            ...prev,
            [nombreProveedor]: prev[nombreProveedor].filter((cat) => cat.path !== path),
          }))
        } else {
          toast.error('Error al eliminar el catálogo')
        }
      },
    })
  }

  const handleVerCatalogo = (catalogo, proveedor) => {
    setCatalogoSeleccionado(catalogo)
    setProveedorCatalogoSeleccionado(proveedor)
  }

  const handleVolverAProveedores = () => {
    setCatalogoSeleccionado(null)
    setProveedorCatalogoSeleccionado(null)
  }

  const handleDescargarCatalogo = (signedUrl, e) => {
    // Detener la propagación para que no abra la vista de detalle
    e.stopPropagation()
    // Abrir el PDF en una nueva pestaña usando la URL firmada
    window.open(signedUrl, '_blank')
  }

  const handleArchivoChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      if (file.type !== 'application/pdf') {
        toast.error('Solo se permiten archivos PDF')
        e.target.value = null // Reset input
        return
      }
      setArchivoSeleccionado(file)
    }
  }

  const handleCerrarModalSubir = () => {
    setModalSubirVisible(false)
    setArchivoSeleccionado(null)
    setProveedorSeleccionado('')
    setModeloSeleccionado('')
    setGrupoComprasSeleccionado('')
    setPlanificadorSeleccionado('')
    setPaisOrigenSeleccionado('China')
    // Reset del input file
    const fileInput = document.getElementById('file-upload')
    if (fileInput) {
      fileInput.value = null
    }
  }

  const obtenerNombreArchivo = (path) => {
    if (!path) return 'Sin nombre'
    const partes = path.split('/')
    return partes[partes.length - 1]
  }

  const contarDocumentos = (nombreProveedor) => catalogosPorProveedor[nombreProveedor]?.length || 0

  const cargarGruposCompras = async () => {
    if (gruposCompras.length > 0) return
    
    setLoadingGruposCompras(true)
    try {
      const response = await getGruposCompras()
      if (response && response.grupo_compras) {
        setGruposCompras(response.grupo_compras)
      }
    } catch (error) {
      toast.error('Error al cargar grupos de compras')
    } finally {
      setLoadingGruposCompras(false)
    }
  }

  const handleAbrirModalSubir = () => {
    setModalSubirVisible(true)
    cargarProveedoresParaModal()
    cargarGruposCompras()
  }

  const handleAbrirModalSubirConProveedor = (proveedor) => {
    setProveedorSeleccionado(proveedor)
    setModalSubirVisible(true)
    cargarGruposCompras()
  }

  // Si hay un catálogo seleccionado, mostrar la vista de detalle
  if (catalogoSeleccionado) {
    return (
      <CatalogoDetalle
        catalogo={catalogoSeleccionado}
        nombreProveedor={proveedorCatalogoSeleccionado}
        onVolver={handleVolverAProveedores}
        idUser={idUser}
      />
    )
  }

  return (
    <div className="boveda-docs-container">
      {/* Header */}
      <div className="boveda-header">
        <div className="boveda-header-content">
          <div className="boveda-title-section">
            <FiFolder className="boveda-title-icon" />
            <div>
              <h1 className="boveda-title">Bóveda de Documentos</h1>
              <p className="boveda-subtitle">
                Gestiona y accede a todos los documentos PDF 
              </p>
            </div>
          </div>
          <button
            type="button"
            className="boveda-btn-primary"
            onClick={handleAbrirModalSubir}
            style={{ display: 'none'}}
          >
            <FiUpload />
            Subir archivo
          </button>
        </div>
      </div>

      {/* Buscador y acciones */}
      <div className="boveda-search-section">
        <div className="boveda-search-container">
          <FiSearch className="boveda-search-icon" />
          <input
            type="text"
            placeholder="Buscar proveedores por nombre o código..."
            className="boveda-search-input"
            value={busqueda}
            onChange={handleBusquedaChange}
          />
          {loadingBusqueda && (
            <div style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)' }}>
              <div className="spinner-border spinner-border-sm" role="status">
                <span className="sr-only">Buscando...</span>
              </div>
            </div>
          )}
        </div>
        <button
          type="button"
          className="boveda-btn-secondary"
          onClick={() => setModalProveedorVisible(true)}
        >
          <FiPlus />
          Nuevo proveedor
        </button>
      </div>

      {/* Lista de proveedores */}
      <div className="boveda-proveedores-list">
        {busqueda.trim().length < 2 ? (
          <div className="boveda-empty-state">
            <FiSearch className="boveda-empty-icon" />
            <p className="boveda-empty-text">Busca un proveedor para comenzar</p>
            <p className="boveda-empty-subtext">
              Escribe al menos 2 caracteres en el buscador para encontrar proveedores
            </p>
          </div>
        ) : loadingBusqueda ? (
          <div className="boveda-empty-state">
            <div className="spinner-border" role="status">
              <span className="sr-only">Buscando...</span>
            </div>
            <p className="boveda-empty-subtext">
              Buscando proveedores...
            </p>
          </div>
        ) : proveedores.length === 0 ? (
          <div className="boveda-empty-state">
            <FiFolder className="boveda-empty-icon" />
            <p className="boveda-empty-text">No se encontraron proveedores</p>
            <p className="boveda-empty-subtext">
              Intenta con otro término de búsqueda o crea un nuevo proveedor
            </p>
          </div>
        ) : (
          proveedores.map((proveedor, index) => {
            const nombreProveedor = proveedor.nombre_proveedor || proveedor.nombre
            return (
              <div key={nombreProveedor || index} className="boveda-proveedor-card">
                {/* Header del proveedor */}
                <div
                  className="boveda-proveedor-header"
                  onClick={() => toggleProveedor(nombreProveedor)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      toggleProveedor(nombreProveedor)
                    }
                  }}
                  role="button"
                  tabIndex={0}
                >
                  <div className="boveda-proveedor-header-left">
                    {proveedoresExpandidos[nombreProveedor] ? (
                      <FiChevronDown className="boveda-chevron" />
                    ) : (
                      <FiChevronRight className="boveda-chevron" />
                    )}
                    <div>
                      <h3 className="boveda-proveedor-nombre">
                        {nombreProveedor}
                      </h3>
                      {proveedor.codigo_proveedor && (
                        <p style={{ fontSize: '12px', color: '#666', margin: 0 }}>
                          Código: {proveedor.codigo_proveedor}
                        </p>
                      )}
                    </div>
                  </div>
                  {proveedoresExpandidos[nombreProveedor] && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div className="boveda-proveedor-contador">
                        {contarDocumentos(nombreProveedor)}{' '}
                        {contarDocumentos(nombreProveedor) === 1
                          ? 'documento'
                          : 'documentos'}
                      </div>
                      <button
                        type="button"
                        className="boveda-btn-primary"
                        style={{ fontSize: '13px', padding: '6px 12px' }}
                        onClick={(e) => {
                          e.stopPropagation()
                          handleAbrirModalSubirConProveedor(proveedor)
                        }}
                        title={`Subir catálogo al proveedor ${nombreProveedor}`}
                      >
                        <FiUpload />
                        Subir catálogo
                      </button>
                    </div>
                  )}
                </div>

                {/* Catálogos (cuando está expandido) */}
                {proveedoresExpandidos[nombreProveedor] && (
                  <div className="boveda-catalogos-grid">
                    {catalogosPorProveedor[nombreProveedor]?.length === 0 ? (
                      <div className="boveda-no-catalogos">
                        <p>No hay catálogos disponibles</p>
                      </div>
                    ) : (
                      catalogosPorProveedor[nombreProveedor]?.map(
                        (catalogo, idx) => {
                          const nombreArchivo = obtenerNombreArchivo(catalogo.path)
                          return (
                            <div
                              key={catalogo.path || idx}
                              className="boveda-catalogo-card"
                              onClick={() => handleVerCatalogo(catalogo, proveedor)}
                              role="button"
                              tabIndex={0}
                              onKeyPress={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  handleVerCatalogo(catalogo, proveedor)
                                }
                              }}
                            >
                              <div className="boveda-catalogo-icon">
                                <FiFile />
                              </div>
                              <div className="boveda-catalogo-info">
                                <h4 className="boveda-catalogo-nombre">
                                  {nombreArchivo}
                                </h4>
                                <p className="boveda-catalogo-fecha">
                                  {nombreProveedor}
                                </p>
                              </div>
                              <div className="boveda-catalogo-actions">
                                <button
                                  type="button"
                                  className="boveda-icon-btn"
                                  onClick={(e) =>
                                    handleDescargarCatalogo(catalogo.signed_url, e)
                                  }
                                  title="Descargar documento"
                                >
                                  <FiDownload />
                                </button>
                                <button
                                  type="button"
                                  className="boveda-icon-btn boveda-icon-btn-danger"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleEliminarCatalogo(
                                      catalogo.path,
                                      nombreArchivo,
                                      nombreProveedor
                                    )
                                  }}
                                  title="Eliminar"
                                >
                                  <FiTrash2 />
                                </button>
                              </div>
                            </div>
                          )
                        }
                      )
                    )}
                  </div>
                )}
              </div>
            )
          })
        )}
      </div>

      {/* Modal para subir documento */}
      <Modal
        title={
          proveedorSeleccionado
            ? `Subir catálogo al proveedor ${proveedorSeleccionado.nombre_proveedor || proveedorSeleccionado.nombre}`
            : 'Subir nuevo documento'
        }
        open={modalSubirVisible}
        onOk={handleSubirCatalogo}
        onCancel={handleCerrarModalSubir}
        okText="Subir documento"
        cancelText="Cancelar"
        confirmLoading={loadingUpload}
        width={500}
      >
        <div className="boveda-modal-content">
          <p className="boveda-modal-subtitle">
            Carga un archivo PDF y completa los detalles requeridos
          </p>

          <div className="boveda-form-group">
            <div className="boveda-file-input-wrapper">
              <input
                type="file"
                accept=".pdf"
                onChange={handleArchivoChange}
                className="boveda-file-input"
                id="file-upload"
              />
              <label htmlFor="file-upload" className="boveda-file-label">
                <FiUpload />
                {archivoSeleccionado
                  ? archivoSeleccionado.name
                  : 'Seleccionar archivo PDF'}
              </label>
            </div>
          </div>

          {!proveedorSeleccionado && (
            <div className="boveda-form-group">
              <label htmlFor="proveedor-select" className="boveda-form-label">
                Proveedor
              </label>
              <select
                id="proveedor-select"
                className="boveda-select"
                value={proveedorSeleccionado ? JSON.stringify(proveedorSeleccionado) : ''}
                onChange={(e) => {
                  const { value } = e.target
                  setProveedorSeleccionado(value ? JSON.parse(value) : '')
                }}
              >
                <option value="">Selecciona un proveedor</option>
                {todosProveedores.map((proveedor, index) => {
                  const nombreProveedor = proveedor.nombre_proveedor || proveedor.nombre
                  const codigoProveedor = proveedor.codigo_proveedor
                  return (
                    <option
                      key={nombreProveedor || index}
                      value={JSON.stringify(proveedor)}
                    >
                      {codigoProveedor ? `${codigoProveedor} - ${nombreProveedor}` : nombreProveedor}
                    </option>
                  )
                })}
              </select>
            </div>
          )}

          <div className="boveda-form-group">
            <label htmlFor="modelo-input" className="boveda-form-label">
              Modelo de Aplicación
            </label>
            <input
              id="modelo-input"
              type="text"
              className="boveda-input"
              placeholder="Ingresa el modelo de aplicación..."
              value={modeloSeleccionado}
              onChange={(e) => setModeloSeleccionado(e.target.value)}
            />
          </div>

          <div className="boveda-form-group">
            <label htmlFor="grupo-compras-input" className="boveda-form-label">
              Grupo de Compras
            </label>
            <input
              id="grupo-compras-input"
              type="text"
              className="boveda-input"
              placeholder="Escribe para buscar grupo de compras..."
              value={grupoComprasSeleccionado}
              onChange={(e) => setGrupoComprasSeleccionado(e.target.value)}
              disabled={loadingGruposCompras}
              list="grupo-compras-list"
            />
            <datalist id="grupo-compras-list">
              {gruposCompras.map((grupo, index) => (
                <option key={grupo.grupo_compras || index} value={grupo.grupo_compras} />
              ))}
            </datalist>
            {loadingGruposCompras && (
              <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                Cargando grupos de compras...
              </p>
            )}
          </div>

          <div className="boveda-form-group">
            <label htmlFor="planificador-input" className="boveda-form-label">
              Planificador
            </label>
            <input
              id="planificador-input"
              type="text"
              className="boveda-input"
              placeholder="Ingresa el planificador..."
              value={planificadorSeleccionado}
              onChange={(e) => setPlanificadorSeleccionado(e.target.value)}
            />
          </div>

          <div className="boveda-form-group">
            <label htmlFor="pais-origen-input" className="boveda-form-label">
              País de Origen
            </label>
            <input
              id="pais-origen-input"
              type="text"
              className="boveda-input"
              placeholder="Ingresa el país de origen..."
              value={paisOrigenSeleccionado}
              onChange={(e) => setPaisOrigenSeleccionado(e.target.value)}
            />
          </div>
        </div>
      </Modal>

      {/* Modal para crear proveedor */}
      <Modal
        title="Crear nuevo proveedor"
        open={modalProveedorVisible}
        onOk={handleCrearProveedor}
        onCancel={() => {
          setModalProveedorVisible(false)
          setNuevoProveedorNombre('')
        }}
        okText="Crear proveedor"
        cancelText="Cancelar"
        confirmLoading={loadingProveedor}
        width={400}
      >
        <div className="boveda-modal-content">
          <div className="boveda-form-group">
            <label htmlFor="nombre-proveedor" className="boveda-form-label">
              Nombre del proveedor
            </label>
            <input
              id="nombre-proveedor"
              type="text"
              className="boveda-input"
              placeholder="Ej: CORSAIR"
              value={nuevoProveedorNombre}
              onChange={(e) => setNuevoProveedorNombre(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleCrearProveedor()
                }
              }}
            />
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default AxiomaBovedaDocsContainer
